import "@babylonjs/loaders/glTF";
import {
  ArcRotateCamera,
  AbstractMesh,
  Bone,
  Color3,
  DirectionalLight,
  Engine,
  HemisphericLight,
  MeshBuilder,
  Quaternion,
  Scene,
  SceneLoader,
  Skeleton,
  Space,
  StandardMaterial,
  TransformNode,
  Vector3
} from "@babylonjs/core";

const JOINT_TO_BONE: Record<number, string[]> = {
  1: ["root", "hips", "pelvis", "mixamorig:hips", "j_bip_c_hips"],
  2: ["left_hip", "leftupleg", "leftthigh", "leftupperleg", "mixamorig:leftupleg", "j_bip_l_upperleg"],
  3: ["right_hip", "rightupleg", "rightthigh", "rightupperleg", "mixamorig:rightupleg", "j_bip_r_upperleg"],
  4: ["spine1", "spine", "mixamorig:spine", "j_bip_c_spine"],
  5: ["left_knee", "leftleg", "leftlowerleg", "leftcalf", "mixamorig:leftleg", "j_bip_l_lowerleg"],
  6: ["right_knee", "rightleg", "rightlowerleg", "rightcalf", "mixamorig:rightleg", "j_bip_r_lowerleg"],
  7: ["spine2", "spine01", "mixamorig:spine1", "j_bip_c_spine1"],
  8: ["left_ankle", "leftfoot", "mixamorig:leftfoot", "j_bip_l_foot"],
  9: ["right_ankle", "rightfoot", "mixamorig:rightfoot", "j_bip_r_foot"],
  10: ["spine3", "spine02", "chest", "upperchest", "mixamorig:spine2", "j_bip_c_chest"],
  11: ["left_foot", "lefttoebase", "lefttoe", "lefttoes", "mixamorig:lefttoebase", "j_bip_l_toe"],
  12: ["right_foot", "righttoebase", "righttoe", "righttoes", "mixamorig:righttoebase", "j_bip_r_toe"],
  13: ["neck", "mixamorig:neck", "j_bip_c_neck"],
  14: ["left_collar", "leftshoulder", "leftcollar", "leftclavicle", "mixamorig:leftshoulder", "j_bip_l_shoulder"],
  15: ["right_collar", "rightshoulder", "rightcollar", "rightclavicle", "mixamorig:rightshoulder", "j_bip_r_shoulder"],
  16: ["head", "mixamorig:head", "j_bip_c_head"],
  17: ["left_shoulder", "leftarm", "leftupperarm", "mixamorig:leftarm", "j_bip_l_upperarm"],
  18: ["right_shoulder", "rightarm", "rightupperarm", "mixamorig:rightarm", "j_bip_r_upperarm"],
  19: ["left_elbow", "leftforearm", "leftlowerarm", "mixamorig:leftforearm", "j_bip_l_lowerarm"],
  20: ["right_elbow", "rightforearm", "rightlowerarm", "mixamorig:rightforearm", "j_bip_r_lowerarm"],
  21: ["left_wrist", "lefthand", "mixamorig:lefthand", "j_bip_l_hand"],
  22: ["right_wrist", "righthand", "mixamorig:righthand", "j_bip_r_hand"],
  23: ["jaw", "jawbone", "mixamorig:jaw", "j_bip_c_jaw"],
  24: ["left_eye_smplhf", "lefteye", "eye_l", "j_bip_l_eye"],
  25: ["right_eye_smplhf", "righteye", "eye_r", "j_bip_r_eye"],
  26: ["left_index1", "lefthandindex1", "leftindex1", "mixamorig:lefthandindex1", "j_bip_l_index1"],
  27: ["left_index2", "lefthandindex2", "leftindex2", "mixamorig:lefthandindex2", "j_bip_l_index2"],
  28: ["left_index3", "lefthandindex3", "leftindex3", "mixamorig:lefthandindex3", "j_bip_l_index3"],
  29: ["left_middle1", "lefthandmiddle1", "leftmiddle1", "mixamorig:lefthandmiddle1", "j_bip_l_middle1"],
  30: ["left_middle2", "lefthandmiddle2", "leftmiddle2", "mixamorig:lefthandmiddle2", "j_bip_l_middle2"],
  31: ["left_middle3", "lefthandmiddle3", "leftmiddle3", "mixamorig:lefthandmiddle3", "j_bip_l_middle3"],
  32: ["left_pinky1", "lefthandpinky1", "leftpinky1", "leftlittle1", "mixamorig:lefthandpinky1", "j_bip_l_little1"],
  33: ["left_pinky2", "lefthandpinky2", "leftpinky2", "leftlittle2", "mixamorig:lefthandpinky2", "j_bip_l_little2"],
  34: ["left_pinky3", "lefthandpinky3", "leftpinky3", "leftlittle3", "mixamorig:lefthandpinky3", "j_bip_l_little3"],
  35: ["left_ring1", "lefthandring1", "leftring1", "mixamorig:lefthandring1", "j_bip_l_ring1"],
  36: ["left_ring2", "lefthandring2", "leftring2", "mixamorig:lefthandring2", "j_bip_l_ring2"],
  37: ["left_ring3", "lefthandring3", "leftring3", "mixamorig:lefthandring3", "j_bip_l_ring3"],
  38: ["left_thumb1", "lefthandthumb1", "leftthumb1", "mixamorig:lefthandthumb1", "j_bip_l_thumb1"],
  39: ["left_thumb2", "lefthandthumb2", "leftthumb2", "mixamorig:lefthandthumb2", "j_bip_l_thumb2"],
  40: ["left_thumb3", "lefthandthumb3", "leftthumb3", "mixamorig:lefthandthumb3", "j_bip_l_thumb3"],
  41: ["right_index1", "righthandindex1", "rightindex1", "mixamorig:righthandindex1", "j_bip_r_index1"],
  42: ["right_index2", "righthandindex2", "rightindex2", "mixamorig:righthandindex2", "j_bip_r_index2"],
  43: ["right_index3", "righthandindex3", "rightindex3", "mixamorig:righthandindex3", "j_bip_r_index3"],
  44: ["right_middle1", "righthandmiddle1", "rightmiddle1", "mixamorig:righthandmiddle1", "j_bip_r_middle1"],
  45: ["right_middle2", "righthandmiddle2", "rightmiddle2", "mixamorig:righthandmiddle2", "j_bip_r_middle2"],
  46: ["right_middle3", "righthandmiddle3", "rightmiddle3", "mixamorig:righthandmiddle3", "j_bip_r_middle3"],
  47: ["right_pinky1", "righthandpinky1", "rightpinky1", "rightlittle1", "mixamorig:righthandpinky1", "j_bip_r_little1"],
  48: ["right_pinky2", "righthandpinky2", "rightpinky2", "rightlittle2", "mixamorig:righthandpinky2", "j_bip_r_little2"],
  49: ["right_pinky3", "righthandpinky3", "rightpinky3", "rightlittle3", "mixamorig:righthandpinky3", "j_bip_r_little3"],
  50: ["right_ring1", "righthandring1", "rightring1", "mixamorig:righthandring1", "j_bip_r_ring1"],
  51: ["right_ring2", "righthandring2", "rightring2", "mixamorig:righthandring2", "j_bip_r_ring2"],
  52: ["right_ring3", "righthandring3", "rightring3", "mixamorig:righthandring3", "j_bip_r_ring3"],
  53: ["right_thumb1", "righthandthumb1", "rightthumb1", "mixamorig:righthandthumb1", "j_bip_r_thumb1"],
  54: ["right_thumb2", "righthandthumb2", "rightthumb2", "mixamorig:righthandthumb2", "j_bip_r_thumb2"],
  55: ["right_thumb3", "righthandthumb3", "rightthumb3", "mixamorig:righthandthumb3", "j_bip_r_thumb3"]
};

const RETARGET_JOINTS = new Set<number>([
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12,
  13, 14, 15, 16, 17, 18, 19, 20, 21, 22,
  23, 24, 25,
  26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40,
  41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55
]);
const ROOT_TRANSLATION_SCALE = 0.025;
const MAX_ROOT_TRANSLATION = 0.18;
const ROTATION_SMOOTHING = 0.35;
type RetargetProfile = "smplx" | "mixamo";

export class BabylonRigRuntime {
  private engine: Engine;
  private scene: Scene;
  private camera: ArcRotateCamera;
  private skeleton: Skeleton | null = null;
  private skeletonRoot: TransformNode | null = null;
  private boneMap = new Map<number, Bone>();
  private restRotations = new Map<Bone, Quaternion>();
  private currentRotations = new Map<Bone, Quaternion>();
  private restRootPosition: Vector3 | null = null;
  private currentRootPosition: Vector3 | null = null;
  private fallbackMeshes: AbstractMesh[] = [];
  private avatarMeshes: AbstractMesh[] = [];
  private profile: RetargetProfile = "mixamo";
  private sourceReferenceTranslation: Vector3 | null = null;
  private sourceReferenceRotations = new Map<number, Quaternion>();
  private appliedFrames = 0;
  private debugFrames = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.engine = new Engine(canvas, true, { preserveDrawingBuffer: false, stencil: false });
    this.scene = new Scene(this.engine);
    this.scene.clearColor.set(0.06, 0.07, 0.08, 1);
    this.camera = new ArcRotateCamera("camera", Math.PI / 2, Math.PI / 2.4, 4.2, new Vector3(0, 1.1, 0), this.scene);
    this.camera.attachControl(canvas, true);
    new HemisphericLight("hemi", new Vector3(0, 1, 0), this.scene).intensity = 0.75;
    const key = new DirectionalLight("key", new Vector3(-0.5, -1, -0.5), this.scene);
    key.intensity = 1.1;
    this.createFallbackAvatar();
    this.engine.runRenderLoop(() => this.scene.render());
    window.addEventListener("resize", () => this.engine.resize());
  }

  async loadAvatar(url: string): Promise<void> {
    const { rootUrl, filename } = splitSceneUrl(url);
    const result = await SceneLoader.ImportMeshAsync("", rootUrl, filename, this.scene);
    result.animationGroups.forEach((group) => group.stop());
    this.scene.stopAllAnimations();
    const skeleton = result.skeletons[0] ?? null;
    if (!skeleton) {
      throw new Error(`Avatar has no skeleton: ${url}`);
    }
    this.fallbackMeshes.forEach((mesh) => mesh.dispose());
    this.fallbackMeshes = [];
    this.avatarMeshes = result.meshes.filter((mesh) => Boolean(mesh.getTotalVertices()));
    this.skeleton = skeleton;
    this.skeletonRoot = findSkeletonRoot(result.meshes, skeleton);
    this.restRootPosition = this.skeletonRoot?.position.clone() ?? null;
    this.currentRootPosition = this.restRootPosition?.clone() ?? null;
    this.boneMap.clear();
    this.restRotations.clear();
    this.profile = detectRetargetProfile(skeleton.bones);
    this.mapBones(skeleton.bones);
    this.captureRestRotations();
    this.resetPose();
    this.frameCameraToMeshes();
    console.info("Avatar runtime loaded", {
      skeleton: {
        name: skeleton.name,
        root: this.skeletonRoot?.name ?? null,
        boneCount: skeleton.bones.length,
        bones: skeleton.bones.map((bone) => bone.name)
      },
      meshes: result.meshes
        .filter((mesh) => "skeleton" in mesh && mesh.skeleton === skeleton)
        .map((mesh) => ({ name: mesh.name, type: mesh.getClassName(), hasSkeleton: Boolean(mesh.skeleton) })),
      matchedBones: [...this.boneMap.entries()].map(([joint, bone]) => ({
        joint,
        bone: bone.name,
        linkedTransform: bone.getTransformNode()?.name ?? null
      })),
      retargetProfile: this.profile
    });
    if (this.boneMap.size === 0) {
      const preview = skeleton.bones.slice(0, 20).map((bone) => bone.name).join(", ");
      throw new Error(`Avatar skeleton loaded, but no configured bones matched. First bones: ${preview}`);
    }
    result.meshes.forEach((mesh) => {
      mesh.scaling.scaleInPlace(1.0);
    });
  }

  applyMotionFrame(frame: Float32Array<ArrayBufferLike>): void {
    if (!this.skeleton) {
      return;
    }
    this.captureSourceReferenceTranslation(frame);
    this.captureSourceReferenceRotations(frame);
    let rawMaxAbs = 0;
    let appliedMaxAbs = 0;
    let appliedBones = 0;
    for (const [joint, bone] of this.boneMap.entries()) {
      if (!RETARGET_JOINTS.has(joint)) {
        continue;
      }
      const offset = joint * 3;
      const rawX = finiteNumber(frame[offset]);
      const rawY = finiteNumber(frame[offset + 1]);
      const rawZ = finiteNumber(frame[offset + 2]);
      rawMaxAbs = Math.max(rawMaxAbs, Math.abs(rawX), Math.abs(rawY), Math.abs(rawZ));
      const remapped = this.remapAxisAngle(joint, rawX, rawY, rawZ);
      const pose = axisAngleToQuaternion(remapped.x, remapped.y, remapped.z);
      const reference = this.sourceReferenceRotations.get(joint);
      const delta = this.profile === "smplx"
        ? pose
        : reference ? Quaternion.Inverse(pose).multiply(reference) : Quaternion.Inverse(pose);
      const deltaAxis = delta.toAxisAngle();
      appliedMaxAbs = Math.max(appliedMaxAbs, Math.abs(deltaAxis.angle));
      this.applyBoneRotation(joint, bone, delta, ROTATION_SMOOTHING);
      appliedBones += 1;
    }
    this.applyRootTranslation(frame);
    this.skeleton.computeAbsoluteMatrices();
    this.skeleton.prepare(true);
    if (this.profile === "smplx") {
      this.centerCameraOnMeshes();
    }
    this.appliedFrames += 1;
    if (this.appliedFrames % 30 === 0) {
      console.info("Applied motion frame", {
        appliedFrames: this.appliedFrames,
        matchedBones: this.boneMap.size,
        appliedBones,
        rawMaxAxisAngle: rawMaxAbs,
        appliedMaxAxisAngle: appliedMaxAbs,
        rotationReference: this.profile === "smplx" ? "direct_pose_rest" : "first_frame_delta",
        axisRemap: this.profile === "smplx" ? "smplx_direct" : "mixamo_apose_profile_v2_premultiply_delta",
        avatarBasePose: "avatar_rest_pose",
        rootTranslationReference: this.sourceReferenceTranslation ? "first_frame" : "none",
        retargetProfile: this.profile
      });
    }
  }

  resetPose(): void {
    this.currentRotations.clear();
    this.sourceReferenceTranslation = null;
    this.sourceReferenceRotations.clear();
    const resetBones = new Set<Bone>();
    for (const [joint, bone] of this.boneMap.entries()) {
      const base = this.getBaseRotation(joint, bone);
      this.writeBoneRotation(bone, base);
      this.currentRotations.set(bone, base.clone());
      resetBones.add(bone);
    }
    for (const [bone, rest] of this.restRotations.entries()) {
      if (!resetBones.has(bone)) {
        this.writeBoneRotation(bone, rest);
        this.currentRotations.set(bone, rest.clone());
      }
    }
    if (this.skeletonRoot && this.restRootPosition) {
      this.skeletonRoot.position.copyFrom(this.restRootPosition);
      this.currentRootPosition = this.restRootPosition.clone();
      this.skeletonRoot.computeWorldMatrix(true);
    }
    this.skeleton?.computeAbsoluteMatrices();
    this.skeleton?.prepare(true);
  }

  private captureSourceReferenceTranslation(frame: Float32Array<ArrayBufferLike>): void {
    if (this.sourceReferenceTranslation) {
      return;
    }
    this.sourceReferenceTranslation = new Vector3(
      finiteNumber(frame[0]),
      finiteNumber(frame[1]),
      finiteNumber(frame[2])
    );
    console.info("Captured source root translation for generic SMPL-X retarget", {
      joints: Math.floor(frame.length / 3),
      rotationReference: "first_frame_delta",
      avatarBasePose: "avatar_rest_pose",
      rootTranslationReference: "first_frame"
    });
  }

  private captureSourceReferenceRotations(frame: Float32Array<ArrayBufferLike>): void {
    if (this.sourceReferenceRotations.size > 0) {
      return;
    }
    for (const joint of this.boneMap.keys()) {
      const offset = joint * 3;
      const rawX = finiteNumber(frame[offset]);
      const rawY = finiteNumber(frame[offset + 1]);
      const rawZ = finiteNumber(frame[offset + 2]);
      const remapped = this.remapAxisAngle(joint, rawX, rawY, rawZ);
      this.sourceReferenceRotations.set(joint, axisAngleToQuaternion(remapped.x, remapped.y, remapped.z));
    }
    console.info("Captured source rotation reference for avatar retarget", {
      joints: this.sourceReferenceRotations.size,
      rotationReference: this.profile === "smplx" ? "direct_pose_rest" : "first_frame_delta",
      axisRemap: this.profile === "smplx" ? "smplx_direct" : "mixamo_apose_profile_v2_premultiply_delta",
      retargetProfile: this.profile
    });
  }

  applyDebugPose(): void {
    if (!this.skeleton) {
      return;
    }
    this.debugFrames += 1;
    const t = this.debugFrames / 12;
    const amount = Math.sin(t) * 1.35;
    const debugJoints = new Map<number, Quaternion>([
      [17, Quaternion.FromEulerAngles(0, 0, amount)],
      [19, Quaternion.FromEulerAngles(amount * 0.6, 0, 0)],
      [18, Quaternion.FromEulerAngles(0, 0, -amount)],
      [20, Quaternion.FromEulerAngles(-amount * 0.6, 0, 0)],
      [2, Quaternion.FromEulerAngles(amount * 0.22, 0, 0)],
      [5, Quaternion.FromEulerAngles(-amount * 0.18, 0, 0)],
      [3, Quaternion.FromEulerAngles(-amount * 0.22, 0, 0)],
      [6, Quaternion.FromEulerAngles(amount * 0.18, 0, 0)],
      [10, Quaternion.FromEulerAngles(0, amount * 0.15, 0)]
    ]);
    const before = this.getDebugBonePosition();
    for (const [joint, delta] of debugJoints.entries()) {
      const bone = this.boneMap.get(joint);
      if (bone) {
        this.applyBoneRotation(joint, bone, delta, 1);
      }
    }
    this.skeleton.computeAbsoluteMatrices();
    this.skeleton.prepare(true);
    if (this.debugFrames % 30 === 0) {
      const after = this.getDebugBonePosition();
      console.info("Applied debug pose", {
        debugFrames: this.debugFrames,
        matchedBones: this.boneMap.size,
        amount,
        sampleBonePositionDelta: before && after ? Vector3.Distance(before, after) : null
      });
    }
  }

  private createFallbackAvatar(): void {
    const material = new StandardMaterial("avatarMaterial", this.scene);
    material.diffuseColor = new Color3(0.28, 0.68, 0.86);
    const body = MeshBuilder.CreateCapsule("body", { height: 1.6, radius: 0.22 }, this.scene);
    body.material = material;
    const head = MeshBuilder.CreateSphere("head", { diameter: 0.32 }, this.scene);
    head.position.y = 1.0;
    head.material = material;
    this.fallbackMeshes = [body, head];
    this.skeleton = new Skeleton("fallbackSkeleton", "fallbackSkeleton", this.scene);
    this.skeletonRoot = null;
    this.restRootPosition = null;
    this.currentRootPosition = null;
    this.restRotations.clear();
    const root = new Bone("root", this.skeleton, null);
    const spine = new Bone("spine", this.skeleton, root);
    const headBone = new Bone("head", this.skeleton, spine);
    const leftUpperLeg = new Bone("leftUpperLeg", this.skeleton, root);
    const leftLowerLeg = new Bone("leftLowerLeg", this.skeleton, leftUpperLeg);
    const leftFoot = new Bone("leftFoot", this.skeleton, leftLowerLeg);
    const rightUpperLeg = new Bone("rightUpperLeg", this.skeleton, root);
    const rightLowerLeg = new Bone("rightLowerLeg", this.skeleton, rightUpperLeg);
    const rightFoot = new Bone("rightFoot", this.skeleton, rightLowerLeg);
    const leftShoulder = new Bone("leftShoulder", this.skeleton, spine);
    const leftUpper = new Bone("leftUpperArm", this.skeleton, leftShoulder);
    const leftLower = new Bone("leftLowerArm", this.skeleton, leftUpper);
    const leftHand = new Bone("leftHand", this.skeleton, leftLower);
    const rightShoulder = new Bone("rightShoulder", this.skeleton, spine);
    const rightUpper = new Bone("rightUpperArm", this.skeleton, rightShoulder);
    const rightLower = new Bone("rightLowerArm", this.skeleton, rightUpper);
    const rightHand = new Bone("rightHand", this.skeleton, rightLower);
    this.mapBones([
      root, spine, headBone,
      leftUpperLeg, leftLowerLeg, leftFoot, rightUpperLeg, rightLowerLeg, rightFoot,
      leftShoulder, leftUpper, leftLower, leftHand, rightShoulder, rightUpper, rightLower, rightHand
    ]);
    this.captureRestRotations();
  }

  private mapBones(bones: Bone[]): void {
    for (const [jointText, names] of Object.entries(JOINT_TO_BONE)) {
      const joint = Number(jointText);
      const bone = bones.find((item) => names.some((name) => boneNameMatches(item.name, name)));
      if (bone) {
        this.boneMap.set(joint, bone);
      }
    }
  }

  private captureRestRotations(): void {
    for (const bone of this.boneMap.values()) {
      const transform = bone.getTransformNode();
      if (transform?.rotationQuaternion) {
        this.restRotations.set(bone, transform.rotationQuaternion.clone());
      } else {
        this.restRotations.set(bone, bone.getRotationQuaternion(Space.LOCAL).clone());
      }
    }
  }

  private applyBoneRotation(joint: number, bone: Bone, delta: Quaternion, smoothing: number): void {
    const base = this.getBaseRotation(joint, bone);
    const target = delta.multiply(base);
    const previous = this.currentRotations.get(bone) ?? base;
    const amount = clamp(smoothing, 0, 1);
    const rotation = amount >= 1 ? target : Quaternion.Slerp(previous, target, amount);
    this.writeBoneRotation(bone, rotation);
    this.currentRotations.set(bone, rotation.clone());
  }

  private getBaseRotation(joint: number, bone: Bone): Quaternion {
    return (this.restRotations.get(bone) ?? Quaternion.Identity()).clone();
  }

  private writeBoneRotation(bone: Bone, rotation: Quaternion): void {
    const transform = bone.getTransformNode();
    if (transform) {
      transform.rotationQuaternion = transform.rotationQuaternion ?? Quaternion.Identity();
      transform.rotationQuaternion.copyFrom(rotation);
      transform.rotation.set(0, 0, 0);
      transform.computeWorldMatrix(true);
      transform.markAsDirty("rotation");
      return;
    }
    bone.setRotationQuaternion(rotation, Space.LOCAL, this.skeletonRoot ?? undefined);
    bone.markAsDirty();
  }

  private applyRootTranslation(frame: Float32Array<ArrayBufferLike>): void {
    if (!this.skeletonRoot || !this.restRootPosition) {
      return;
    }
    const ref = this.sourceReferenceTranslation ?? Vector3.Zero();
    const scale = this.profile === "smplx" ? 1.0 : ROOT_TRANSLATION_SCALE;
    const maxTranslation = this.profile === "smplx" ? Number.POSITIVE_INFINITY : MAX_ROOT_TRANSLATION;
    const x = clamp((finiteNumber(frame[0]) - ref.x) * scale, -maxTranslation, maxTranslation);
    const y = clamp((finiteNumber(frame[1]) - ref.y) * scale, -maxTranslation, maxTranslation);
    const z = clamp((finiteNumber(frame[2]) - ref.z) * scale, -maxTranslation, maxTranslation);
    const target = this.restRootPosition.add(new Vector3(x, y, z));
    const previous = this.currentRootPosition ?? this.restRootPosition;
    const position = Vector3.Lerp(previous, target, ROTATION_SMOOTHING);
    this.skeletonRoot.position.copyFrom(position);
    this.skeletonRoot.computeWorldMatrix(true);
    this.currentRootPosition = position;
  }

  private remapAxisAngle(joint: number, x: number, y: number, z: number): Vector3 {
    return this.profile === "smplx" ? new Vector3(x, y, z) : remapAxisAngleForAvatar(joint, x, y, z);
  }

  private frameCameraToMeshes(): void {
    const bounds = computeMeshBounds(this.avatarMeshes);
    if (!bounds) {
      return;
    }
    const size = bounds.max.subtract(bounds.min);
    const center = bounds.min.add(size.scale(0.5));
    const radius = Math.max(size.length() * 1.05, 1.25);
    this.camera.setTarget(center);
    this.camera.radius = radius;
    this.camera.lowerRadiusLimit = radius * 0.25;
    this.camera.upperRadiusLimit = radius * 5;
  }

  private centerCameraOnMeshes(): void {
    const bounds = computeMeshBounds(this.avatarMeshes);
    if (!bounds) {
      return;
    }
    this.camera.setTarget(bounds.min.add(bounds.max.subtract(bounds.min).scale(0.5)));
  }

  private getDebugBonePosition(): Vector3 | null {
    const bone = this.boneMap.get(21) ?? this.boneMap.get(14) ?? null;
    if (!bone) {
      return null;
    }
    const transform = bone.getTransformNode();
    if (transform) {
      return transform.getAbsolutePosition().clone();
    }
    return bone.getAbsolutePosition(this.skeletonRoot);
  }
}

function boneNameMatches(actual: string, expected: string): boolean {
  const actualName = normalizeBoneName(actual);
  const expectedName = normalizeBoneName(expected);
  return actualName === expectedName || actualName.endsWith(expectedName);
}

function normalizeBoneName(name: string): string {
  return name
    .toLowerCase()
    .replace(/^armature[.:_/ -]*/u, "")
    .replace(/^mixamorig[.:_/ -]*/u, "")
    .replace(/[^a-z0-9]/gu, "");
}

function detectRetargetProfile(bones: Bone[]): RetargetProfile {
  const names = new Set(bones.map((bone) => normalizeBoneName(bone.name)));
  const smplxMatches = [
    "pelvis",
    "lefthip",
    "righthip",
    "spine1",
    "leftshoulder",
    "rightshoulder",
    "leftindex1",
    "rightindex1"
  ].filter((name) => names.has(name)).length;
  return smplxMatches >= 5 ? "smplx" : "mixamo";
}

function computeMeshBounds(meshes: AbstractMesh[]): { min: Vector3; max: Vector3 } | null {
  let min: Vector3 | null = null;
  let max: Vector3 | null = null;
  for (const mesh of meshes) {
    mesh.computeWorldMatrix(true);
    const box = mesh.getBoundingInfo().boundingBox;
    min = min ? Vector3.Minimize(min, box.minimumWorld) : box.minimumWorld.clone();
    max = max ? Vector3.Maximize(max, box.maximumWorld) : box.maximumWorld.clone();
  }
  if (!min || !max) {
    return null;
  }
  return { min, max };
}

function axisAngleToQuaternion(x: number, y: number, z: number): Quaternion {
  const angle = Math.hypot(x, y, z);
  if (angle < 1e-6) {
    return Quaternion.Identity();
  }
  return Quaternion.RotationAxis(new Vector3(x / angle, y / angle, z / angle), angle);
}

function remapAxisAngleForAvatar(joint: number, x: number, y: number, z: number): Vector3 {
  if (joint >= 14 && joint <= 22) {
    return new Vector3(x, -z, y);
  }
  if (joint >= 26 && joint <= 55) {
    return new Vector3(x, -z, y);
  }
  return new Vector3(x, y, z);
}

function finiteNumber(value: number): number {
  return Number.isFinite(value) ? value : 0;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function splitSceneUrl(url: string): { rootUrl: string; filename: string } {
  const parsed = new URL(url);
  const slash = parsed.pathname.lastIndexOf("/");
  const filename = `${parsed.pathname.slice(slash + 1)}${parsed.search}`;
  parsed.pathname = parsed.pathname.slice(0, slash + 1);
  parsed.search = "";
  parsed.hash = "";
  return { rootUrl: parsed.toString(), filename };
}

function findSkeletonRoot(meshes: AbstractMesh[], skeleton: Skeleton): TransformNode | null {
  const skinned = meshes.find((mesh) => "skeleton" in mesh && mesh.skeleton === skeleton);
  return skinned ?? meshes[0] ?? null;
}
